import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Flame, Download, Heart, TrendingUp, GitBranch, RefreshCw, Search } from "lucide-react";
import { CmdLine } from "../components/InstallCommands";
import { api } from "../lib/api";
import { useCatalog } from "../lib/useCatalog";
import { useHardware } from "../context/HardwareContext";
import { evaluate, recommendedQuant } from "../lib/compute";
import { VerdictBadge } from "../components/VerdictBadge";
import { FavoriteButton, trendingFavorite } from "../components/FavoriteButton";
import { TrendingFilters, sizeOf } from "../components/TrendingFilters";

function fmt(n) {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${n}`;
}

export const PIPELINE_LABEL = {
  "text-generation": "Texte",
  "image-text-to-text": "Vision + Texte",
  "text2text-generation": "Seq2Seq",
};

function estimate(m, quantizations, hardware) {
  if (!m.params || !quantizations) return null;
  const model = { params: m.params };
  return evaluate(model, recommendedQuant(model, hardware, quantizations), hardware);
}

function TrendingCard({ m, ev }) {
  return (
    <div data-testid="trending-card" className="flex min-w-0 flex-col rounded-xl border border-[#1E2638] bg-[#0F141C] p-5 transition-all hover:border-primary/70">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono2 text-[11px] text-primary">{m.author}</p>
          <h3 className="truncate font-head text-base font-bold leading-tight" title={m.name}>{m.name}</h3>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {ev && <VerdictBadge verdict={ev.verdict} data-testid="trending-verdict-badge" />}
          <FavoriteButton item={trendingFavorite(m)} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-3 font-mono2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-orange-400" /> {fmt(m.trendingScore)}</span>
        <span className="flex items-center gap-1"><Download className="h-3.5 w-3.5" /> {fmt(m.downloads)}</span>
        <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {fmt(m.likes)}</span>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {m.pipeline_tag && (
          <span className="rounded border border-sky-900 bg-sky-950/40 px-1.5 py-0.5 font-mono2 text-[10px] uppercase text-sky-400">
            {PIPELINE_LABEL[m.pipeline_tag] || m.pipeline_tag}
          </span>
        )}
        {m.params && <span className="rounded border border-slate-800 bg-slate-900/40 px-1.5 py-0.5 font-mono2 text-[10px] text-slate-300">{m.params}B</span>}
        {m.license && <span className="rounded border border-emerald-900 bg-emerald-950/40 px-1.5 py-0.5 font-mono2 text-[10px] uppercase text-emerald-400">{m.license}</span>}
      </div>

      {ev && <p className="mt-3 font-mono2 text-xs text-muted-foreground">Estimé ~{ev.requiredGB.toFixed(1)} Go pour votre matériel</p>}

      <div className="mt-3" data-testid="trending-install">
        {m.ollama ? (
          <CmdLine label="Prêt pour Ollama" cmd={`ollama run ${m.ollama}`} />
        ) : (
          <a href={`https://huggingface.co/models?other=base_model:quantized:${m.id}`} target="_blank" rel="noopener noreferrer" data-testid="trending-gguf-search-link"
            className="flex items-center gap-1.5 text-xs text-primary hover:underline">
            <Search className="h-3.5 w-3.5" /> Trouver une version prête pour Ollama / LM Studio
          </a>
        )}
      </div>

      <div className="mt-auto flex items-center gap-2 pt-4">
        <a href={`https://huggingface.co/${m.id}`} target="_blank" rel="noopener noreferrer" data-testid="trending-hf-link"
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
          <GitBranch className="h-3.5 w-3.5" /> Voir sur Hugging Face
        </a>
      </div>
    </div>
  );
}

export default function Trending() {
  const { data: catalog } = useCatalog();
  const { hardware } = useHardware();
  const [filters, setFilters] = useState({ fit: "all", task: "all", size: "all", ready: "all" });
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["trending"],
    queryFn: async () => (await api.get("/trending", { params: { limit: 40 } })).data,
    staleTime: 5 * 60 * 1000,
  });

  const rows = useMemo(() => {
    if (!data?.models) return [];
    return data.models
      .map((m) => ({ m, ev: estimate(m, catalog?.quantizations, hardware) }))
      .filter(({ m, ev }) => {
        if (filters.task !== "all" && (m.pipeline_tag || "other") !== filters.task) return false;
        if (filters.size !== "all" && sizeOf(m.params) !== filters.size) return false;
        if (filters.ready === "ollama" && !m.ollama) return false;
        if (filters.fit === "can_run" && ev?.verdict !== "can_run") return false;
        if (filters.fit === "runs" && !(ev && ev.verdict !== "no_go")) return false;
        return true;
      });
  }, [data, catalog, hardware, filters]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <span className="flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-orange-400">
            <Flame className="h-4 w-4" /> En direct de Hugging Face
          </span>
          <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight">Modèles à la une</h1>
          <p className="mt-1 text-muted-foreground">Les modèles open source les plus tendance en ce moment, avec estimation pour votre matériel quand la taille est connue.</p>
        </div>
        <button onClick={() => refetch()} disabled={isFetching} data-testid="trending-refresh"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-[#1E2638] bg-[#0F141C] px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary">
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Actualiser
        </button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Chargement du flux…</p>
      ) : isError || (data && data.error && !data.models?.length) ? (
        <div className="rounded-xl border border-red-900 bg-red-950/30 p-4 text-sm text-red-400">
          Impossible de contacter Hugging Face pour le moment. Réessayez dans un instant.
        </div>
      ) : (
        <>
          <TrendingFilters filters={filters} setFilters={setFilters} models={data.models} count={rows.length} />
          {rows.length === 0 ? (
            <p data-testid="trending-empty" className="rounded-xl border border-[#1E2638] bg-[#0F141C] p-6 text-sm text-muted-foreground">
              Aucun modèle tendance ne correspond à ces filtres. Élargissez la taille ou la compatibilité.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map(({ m, ev }) => <TrendingCard key={m.id} m={m} ev={ev} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
